import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect } from 'react';

import {
  PAGE_TYPE,
  Referrer,
} from '@invideoio/web-shared/utils/utils.constant';

import VideoWallCategoryTemplateHead from '@components/video-wall-category-templates/VideoWallCategoryTemplateHead';
import VideoWallCategoryTemplateLeftPanel from '@components/video-wall-category-templates/VideoWallCategoryTemplateLeftPanel';
import VideoWallCategoryTemplates from '@components/video-wall-category-templates/VideoWallCategoryTemplates';
import HomepageSEO from '@components/common/SEO/HomepageSEO';
import HeaderWithCTA from '@components/header-ai';
import { footerExploreLinks } from '@constants/footerExplore';
import { TEMPLATE_LIMIT } from '@constants/templates';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import Footer from '@invideoio/web-shared/components/Footer';
import {
  TemplateSearchData,
  TemplateSearchPayload,
  TemplateSearchQuery,
  TemplateSearchResponse,
} from '@invideoio/web-shared/types/Templates';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import AppLayout from '@layouts/AppLayout';
import {
  fetchAllVideoWallTemplateCategories,
  searchVideoWallTemplates,
} from '@lib/API/template.api';
import { StateContext } from '@store/store';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { parseFiltersFromQueryParams } from '@utils/template-category/filter.utils';
import { processTemplateSearchAPIResponse } from '@utils/template-category/template.utils';
import dynamic from 'next/dynamic';
import { BrowserType } from '@invideoio/web-shared/constants/common';
import StudioBanner from '@invideoio/web-shared/components/StudioBanner';
import {
  fetchAndEnhanceAllVideoTemplateData,
  fetchAndEnhanceVideoTemplateData,
} from '@utils/common.utils';

const FixedBanner = dynamic(
  () =>
    import(
      /* webpackChunkName: "FixedBanner" */
      '@invideoio/web-shared/components/FixedBanner'
    ),
);
interface TemplateCategoryPageProps {
  templateList: TemplateSearchResponse;
  filters?: any;
  templatePageData: any;
  templateCategories: any[];
  currentPage: number;
  browser: BrowserType;
}

function VideoWallTemplateCategory(props: TemplateCategoryPageProps) {
  const {
    templateList,
    filters,
    templatePageData,
    templateCategories,
    currentPage,
    browser,
  } = props;

  const globalState = useContext(StateContext);
  const { t, lang } = useTranslation('home');
  const { platform, os, config } = globalState;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    AnalyticsUtils.registerPage(PAGE_TYPE.VIDEOWALL_CATEGORY as any, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  return (
    <AppLayout platform={platform}>
      <HomepageSEO seo={templatePageData.SEO} />
      {/* <StudioBanner browser={browser} /> */}
      <HeaderWithCTA isCTAIntersected={true} />

      <VideoWallCategoryTemplateHead Head={templatePageData.Head} />

      <div
        className={ivclass(
          'iv-relative iv-overflow-x-hidden',
          'iv-flex md:iv-flex-col sm:iv-flex-col',
          'iv-px-40 sm:iv-px-25',
        )}
      >
        {/* <VideoWallCategoryTemplateLeftPanel
          templateCategories={templateCategories}
          templateCategoryName={templatePageData.CategoryName}
        /> */}

        <VideoWallCategoryTemplates
          templateList={templatePageData.videoWallTemplate}
          filters={filters}
          totalCount={templatePageData.videoWallTemplate.length}
          currerntPage={currentPage}
          platform={platform}
          moreLink={templatePageData.TemplateSearchFilters}
        />
      </div>

      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        exploreLinks={footerExploreLinks[config?.env?.siteId][lang].links}
      />
    </AppLayout>
  );
}

export default VideoWallTemplateCategory;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const query = context.query;

  const templatePageData = {
    SEO: {
      Title: 'Made with love, using Invideo',
      Description: '',
      CanonicalURL: 'https://invideo.io/ai-videos/',
      Hreflang: [],
    },
    Head: {
      Title: 'Made with love, using invideo AI',
      HighlightedTitle: '',
      SubTitle: 'Showcasing our user-stories, one video at a time.',
    },
    videoWallTemplate: [] as Array<any>,
  };

  let page: number | string | string[] | undefined = query.page;

  if (typeof page === 'string') {
    page = parseInt(page);
  } else {
    page = 1;
  }

  const templateCategories = await fetchAllVideoWallTemplateCategories();

  let templateList: TemplateSearchData | null = null;

  let filters;
  filters = {} as TemplateSearchQuery;
  filters.search_term = 'ai';

  if (templatePageData.videoWallTemplate) {
    await fetchAndEnhanceAllVideoTemplateData(templatePageData);
  }

  if (templatePageData.videoWallTemplate?.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      templatePageData,
      templateList,
      filters,
      templateCategories,
      currentPage: page,
    },
  };
}
